import { AsyncPipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatAccordion } from '@angular/material/expansion';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import {
  BypassSecurityPipe,
  ByteToMegaBytePipe,
  ConverterService,
  Deferred,
  EinreichungComponents,
  EndToEndEncrypter,
  LogService,
  MessageBase,
  REGEX,
  SimpleProgressComponent,
} from '@ebeihilfe/core';
import {
  Antrag,
  Beleg,
  BelegSelectionBase,
  CanSendAntragPipe,
  CurrentSizeInBytePipe,
  EinreichungComponentType,
  EinreichungStepResultType,
  HasAllMandatoryPagesPipe,
  MaxSizeExceededPipe,
  PercentageOfMaxSizePipe,
  Seite,
} from '@ebeihilfe/einreichung-old';
import { EinreichungApiService } from '@ebeihilfe/einreichung-shared/services';
import { ModalService, OverlayService } from '@ebeihilfe/web-logic';
import { TranslateModule } from '@ngx-translate/core';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFPageProxy } from 'pdfjs-dist';

import { BelegComponent } from './beleg/beleg.component';

class PdfProgressUiHelper {
  public allFiles?: number;
  public current?: number;
  public text?: string;

  constructor() {
    this.allFiles = 0;
    this.current = 0;
    this.text = '';
  }
}

@Component({
  selector: 'portal-beleg-selection',
  templateUrl: './beleg-selection.component.html',
  styleUrls: ['./beleg-selection.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SimpleProgressComponent,
    BypassSecurityPipe,
    CurrentSizeInBytePipe,
    MaxSizeExceededPipe,
    PercentageOfMaxSizePipe,
    ByteToMegaBytePipe,
    AsyncPipe,
    DecimalPipe,
    TranslateModule,
    MatAccordion,
    CanSendAntragPipe,
    HasAllMandatoryPagesPipe,
    MatButton,
    MatCard,
    MatCardContent,
    ReactiveFormsModule,
    BelegComponent,
  ],
})
export class BelegSelectionComponent
  extends BelegSelectionBase
  implements EinreichungComponentType<BelegSelectionComponent>, OnInit
{
  private readonly cdr = inject(ChangeDetectorRef, { optional: true });
  private readonly converterService = inject(ConverterService);
  private readonly einreichungApiService = inject(EinreichungApiService);
  private readonly endToEndEncrypter = inject(EndToEndEncrypter);
  private readonly formBuilder = inject(FormBuilder);
  private readonly message = inject(MessageBase);
  private readonly modals = inject(ModalService);
  private readonly spinnerService = inject(OverlayService);

  public component = BelegSelectionComponent;
  public einreichungsSpinnerData = viewChild<TemplateRef<string>>('einreichungsSpinnerData');
  public fileInput = viewChild<ElementRef>('fileInput');
  public focusMoveUpBtn: Record<string, never>;
  public name = EinreichungComponents.BelegSelection;
  public pdfProgress: PdfProgressUiHelper = {
    allFiles: 0,
    current: 0,
    text: '',
  };
  public showPureTextOnly = false;
  public text: string;
  public uploadFileform: FormGroup;

  public async addPageToBeleg(beleg: Beleg): Promise<void> {
    this.currentActiveBeleg = beleg;
    this.fileInput().nativeElement.click();
  }

  public canHandle(antrag: Antrag): boolean | Promise<boolean> {
    return antrag.hasAntragsMetadata();
  }

  public async fileInputOnChangeEvent(event: Event): Promise<void> {
    if (!event) {
      return;
    }

    this.changeEinreichungsSpinnerText(_('waiting_text.loading_image'));
    this.spinnerService.show(this.einreichungsSpinnerData());

    const files = Array.from((event.target as HTMLInputElement).files);
    for (const f of files) {
      if (this.shouldSkipFile(f)) {
        continue;
      }

      if (f.type === 'application/pdf') {
        try {
          this.changePDFProgressInfo({ text: _('new_antrag.pdf.read_into_memory') });
          const completePdfFileAsUint8 = await this.convertFileInputToArraybuffer(f);
          const pdfFilesAsBlobArray = await this.splitFileFromUintArray(completePdfFileAsUint8);
          await this.processArray(pdfFilesAsBlobArray);
        } catch (err) {
          await this.handlePdfProcessingError(err, f);
        }

        this.changeEinreichungsSpinnerText(_('waiting_text.adding_images'));
      } else {
        try {
          const file = await this.prepareForDES(f);
          await this.addImageToArray(file);
        } catch (exception) {
          LogService.error(JSON.stringify(exception));
          await this.message.showInfo({
            message: _('new_antrag.message.can_not_read_image'),
            messageParam: {
              fileName: f.name,
            },
          });
        }
      }
    }

    this.fileInput().nativeElement.value = '';
    this.spinnerService.hide();
  }

  public handle(
    antrag: Antrag,
    instance: BelegSelectionComponent,
    work: Deferred<EinreichungStepResultType>,
  ): Promise<EinreichungStepResultType> {
    instance.antrag = antrag;
    instance.work = work;
    return instance.work;
  }

  public override moveBelegToTop(beleg: Beleg): void {
    super.moveBelegToTop(beleg);
    this.focusMoveUpBtn = {};
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.uploadFileform = this.formBuilder.group({});
    if (!this.antrag.hasBelege()) {
      this.antrag.addBeleg();
    }
  }

  public async openImageDetailView(seite: Seite): Promise<void> {
    const imageBlob = await seite.getFile();
    const changedImageBlob = await this.modals.openImageModal(imageBlob, true);

    if (changedImageBlob) {
      try {
        await seite.setFile(changedImageBlob);
        this.cdr.markForCheck();
      } catch (e) {
        LogService.error(e);
        await this.message.showError({ message: 'new_antrag.text.error_while_saving_image' });
      }
    }
  }

  public async openDeleteBelegModal(beleg: Beleg): Promise<void> {
    const confirmationResult = await this.modals.openConfirmationModal({
      content: 'text.shallDeleteBeleg',
    });

    if (confirmationResult) {
      this.antrag?.removeBeleg(beleg);
      this.cdr.markForCheck();
    }
  }

  public async interruptAntrag(): Promise<void> {
    const shouldInterrupt = await this.modals.openConfirmationModal({
      content: this.translate.instant(_('new_antrag.text.shall_interrupt')),
    });

    if (!shouldInterrupt) {
      return;
    }

    if (this.antrag) {
      this.antrag.doNotFinishMe = true;
    }
    this.finishStep();
  }

  private async addImageToArray(file: Blob): Promise<void> {
    const seite = new Seite(this.converterService, this.einreichungApiService, this.endToEndEncrypter);
    await seite.setFile(file);

    this.currentActiveBeleg?.addSeite(seite);
    this.cdr.markForCheck();
  }

  private changeEinreichungsSpinnerText(text: string): void {
    this.showPureTextOnly = true;
    this.text = this.translate.instant(text);
  }

  private changePDFProgressInfo(updatedData: PdfProgressUiHelper): void {
    this.showPureTextOnly = false;
    this.pdfProgress = {
      ...this.pdfProgress,
      ...updatedData,
    };
  }

  private convertFileInputToArraybuffer(pdfFile: File): Promise<Uint8Array> {
    const fileReader = new FileReader();

    return new Promise((resolve: (value: PromiseLike<Uint8Array> | Uint8Array) => void): void => {
      fileReader.onload = async function (): Promise<void> {
        const typedArray = new Uint8Array(this.result as ArrayBuffer);
        resolve(typedArray);
      };
      fileReader.onerror = (error): void => {
        LogService.error(JSON.stringify(error));
      };
      fileReader.readAsArrayBuffer(pdfFile);
    });
  }

  private async createBlobFromPDFProxy(page: PDFPageProxy): Promise<Blob> {
    const viewport = page.getViewport({ scale: 5 });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const renderTask = page.render({
      canvasContext: ctx,
      viewport,
    });
    await renderTask.promise;
    return new Promise((resolve) => canvas.toBlob(resolve));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async handlePdfProcessingError(err: any, f: File): Promise<void> {
    if (err.name === 'InvalidPDFException') {
      await this.message.showInfo({
        message: _('new_antrag.message.invalid_pdf'),
        messageParam: {
          fileName: f.name,
        },
      });
    } else if (err.name === 'PasswordException') {
      await this.message.showInfo({
        message: _('new_antrag.message.no_password_protected_files'),
        messageParam: {
          fileName: f.name,
        },
      });
    } else {
      LogService.error(err.toString());
      await this.message.showError({ message: '' });
    }
  }

  private isImageTooSmall(size: number, name: string): boolean {
    if (size < this.minimumSeiteSizeInByte) {
      this.message.showInfo({
        message: 'new_antrag.message.file_under_filesize',
        messageParam: {
          fileName: name,
          minFileSizeInKB: this.minimumSeiteSizeInByte / 1024,
        },
      });
      return true;
    }
    return false;
  }

  private prepareForDES(blob: Blob): Promise<Blob> {
    return new Promise<Blob>((resolve: (value: Blob) => void, reject: (value: ErrorEvent) => void): void => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onerror = (err: ErrorEvent): void => {
        reject(err);
      };
      img.onload = (): void => {
        const ratio = BelegSelectionComponent.getRatio(img.width, img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.8);
      };
      img.src = url;
    });
  }

  private async processArray(array: Blob[]): Promise<void> {
    this.changePDFProgressInfo({
      text: _('new_antrag.pdf.creating_thumb'),
      allFiles: array.length,
    });

    for (let i = 0, len = array.length; i < len; i++) {
      const image = await this.prepareForDES(array[i]);
      this.changePDFProgressInfo({ current: i + 1 });
      await this.addImageToArray(image);
    }
  }

  private shouldSkipFile(f: File): boolean {
    let errored = false;
    const size = f.size / 1024 / 1024;
    if (size > this.maximumFileSizeInMb) {
      this.message.showInfo({
        message: _('new_antrag.message.file_above_filesize'),
        messageParam: {
          fileName: f.name,
          maxFileSizeinMb: this.maximumFileSizeInMb,
        },
      });
      errored = true;
    }

    const isPdf = REGEX.INPUT_PDF.test(f.type);
    const isImage = REGEX.INPUT_IMAGE.test(f.type);

    if (!(isPdf || isImage)) {
      this.message.showInfo({
        message: 'new_antrag.message.only_images_and_pdf',
        messageParam: {
          fileName: f.name,
        },
      });
      errored = true;
    }

    if (isImage && this.isImageTooSmall(f.size, f.name)) {
      errored = true;
    }
    return errored;
  }

  private async splitFileFromUintArray(pdfFile: Uint8Array): Promise<Blob[]> {
    const files: Blob[] = [];

    const loadingTask = pdfjsLib.getDocument(pdfFile);
    const pdf = await loadingTask.promise;
    this.changePDFProgressInfo({
      text: _('new_antrag.pdf.to_image'),
      allFiles: pdf.numPages,
    });

    for (let i = 0, len = pdf.numPages; i < len; i++) {
      const current = i + 1;
      this.changePDFProgressInfo({ current: current });
      const page = await pdf.getPage(current);
      files[i] = await this.createBlobFromPDFProxy(page);
    }
    return files;
  }

  private static getRatio(width: number, height: number): number {
    const maxHeight = 2480;
    const maxWidth = 3508;
    let ratio = 1;
    if (width > maxWidth) {
      ratio = maxWidth / width;
    }
    if (height > maxHeight) {
      const temporaryRatio = maxHeight / height;
      ratio = temporaryRatio < ratio ? temporaryRatio : ratio;
    }
    return Math.min(ratio, 1);
  }
}
